import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Slider,
} from '@mui/material';
import { Task, TaskStatus, CreateTaskRequest } from '../types/task';
import { TaskService } from '../services/api';

interface EditTaskDialogProps {
  open: boolean;
  task: Task | null;
  onClose: () => void;
  onSave: (task: Task) => void;
  onTaskCreated?: () => void;
}

const defaultTask = {
  title: '',
  description: '',
  status: TaskStatus.TODO,
  dueDate: new Date().toISOString().slice(0, 16),
  priority: 5,
};

const EditTaskDialog: React.FC<EditTaskDialogProps> = ({
  open,
  task,
  onClose,
  onSave,
  onTaskCreated,
}) => {
  const [editedTask, setEditedTask] = useState<any>(defaultTask);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (task) {
      // Editing an existing task
      setIsCreating(false);
      
      // Format the date for the datetime-local input
      const date = new Date(task.dueDate);
      const formattedDate = date.toISOString().slice(0, 16); // Get YYYY-MM-DDTHH:MM format
      
      setEditedTask({
        ...task,
        dueDate: formattedDate,
      });
    } else {
      // Creating a new task
      setIsCreating(true);
      setEditedTask(defaultTask);
    }
  }, [task, open]);

  const handleChange = (field: string, value: any) => {
    setEditedTask({
      ...editedTask,
      [field]: value,
    });
  };

  const handleSubmit = async () => {
    if (isCreating) {
      // Creating a new task
      try {
        const createTaskData: CreateTaskRequest = {
          title: editedTask.title,
          description: editedTask.description,
          status: editedTask.status,
          dueDate: editedTask.dueDate,
          priority: editedTask.priority
        };
        
        await TaskService.createTask(createTaskData);
        onClose();
        if (onTaskCreated) {
          onTaskCreated();
        }
      } catch (error) {
        console.error('Failed to create task:', error);
      }
    } else {
      // Updating an existing task
      onSave(editedTask);
    }
  };

  const priorityMarks = [
    { value: 1, label: 'Highest' },
    { value: 5, label: 'Medium' },
    { value: 10, label: 'Lowest' },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isCreating ? 'Create New Task' : 'Edit Task'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Title"
          fullWidth
          value={editedTask.title}
          onChange={(e) => handleChange('title', e.target.value)}
        />
        <TextField
          margin="dense"
          label="Description"
          fullWidth
          multiline
          rows={4}
          value={editedTask.description}
          onChange={(e) => handleChange('description', e.target.value)}
        />
        <TextField
          margin="dense"
          label="Due Date"
          type="datetime-local"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={editedTask.dueDate}
          onChange={(e) => handleChange('dueDate', e.target.value)}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Status</InputLabel>
          <Select
            value={editedTask.status}
            label="Status"
            onChange={(e) => handleChange('status', e.target.value)}
          >
            {Object.values(TaskStatus).map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ mt: 3, mb: 1 }}>
          <Typography gutterBottom>Priority Level (1-10)</Typography>
          <Slider
            value={editedTask.priority}
            min={1}
            max={10}
            step={1}
            marks={priorityMarks}
            valueLabelDisplay="auto"
            onChange={(_, value) => handleChange('priority', value as number)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          {isCreating ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTaskDialog; 